import { isPlatform } from '@ionic/react';
import moment from 'moment-timezone';
import latinize from 'latinize';
import { env, types } from 'utils';
import { Browser } from '@capacitor/browser';
import { Capacitor } from '@capacitor/core';
import _ from 'lodash';
import { parsePhoneNumber } from 'awesome-phonenumber';
import { User } from './types';

moment.tz.setDefault('Africa/Lagos');

export const config = {
	apiURL: env.API_URL,
	version: {
		apk: '1.3.04',
		ios: '1.3.04',
		pwa: '1.3.04',
	},
	platform: 'apk',
};

export const init = () => {
	config['platform'] = isMobile ? (isPlatform('ios') ? 'ios' : 'apk') : 'pwa';
};

export const app = {
	version:
		config.version[config.platform as keyof typeof config.version] || '1.0.0',
	dbpref: 'cr_aarin_',
};

export const dates = {
	yr: moment().format('YYYY'),
	dt: moment().format('YYYY-MM-DD'),
};

export const isMobile = Capacitor.isNativePlatform();

// ::: storage
export const setStorage = (key: string, value: any) => {
	if (key) {
		localStorage.setItem(app.dbpref + key, value);
	}
};
export const getStorage = (key: string) => {
	const value = localStorage.getItem(app.dbpref + key);
	return value || '';
};
export const setStorageJson = (key: string, value: any, onOK: any = null) => {
	if (key && value) {
		localStorage.setItem(app.dbpref + key, JSON.stringify(value));
		onOK && onOK();
	}
};
export const getStorageJson = (key: string) => {
	if (key) {
		const value = localStorage.getItem(app.dbpref + key) || '';
		return isJson(value) ? JSON.parse(value) : '';
	}
};
export const delStorage = (key: string) => {
	if (key) {
		localStorage.removeItem(app.dbpref + key);
	}
};

export const inArray = (needle: any, haystack: any): boolean => {
	const length = haystack.length;
	for (let i = 0; i < length; i++) {
		if (haystack[i] === needle) return true;
	}
	return false;
};
export const mergeObj = (obj: any, src: any) => {
	for (const key in src) {
		if (src.hasOwnProperty(key)) obj[key] = src[key];
	}
	return obj;
};
export const getFileExtension = (filename: string) => {
	const ext = /^.+\.([^.]+)$/.exec(filename);
	return ext === null ? '' : ext[1];
};

export const redirect = (to: any) => {
	// return false;
	window.location = to;
};

export const generateOptions = (length: number, step = 1) => {
	const arr = [];
	for (let value = 0; value < length; value += step) {
		arr.push(value);
	}
	return arr;
};

export const numberFormat = (number: number, decimal: number = 2) => {
	// eslint-disable-next-line no-useless-escape
	number = +(number || 0).toString().replace(/[^0-9\./]/g, '');
	return new Intl.NumberFormat('en-US', {
		minimumFractionDigits: 0,
		maximumFractionDigits: decimal,
	}).format(number);
};

export const isToday = (date: string) =>
	!!(moment(date).format('YYYY-MM-DD') === moment().format('YYYY-MM-DD'));

export const format = {
	date: (date: string) =>
		isToday(date)
			? `Today @ ${moment(date).format('H:mm A')}`
			: moment(date).format('MMM DD, YYYY HH:mm:ss'),
	number: (number: number, decimal = 3) => {
		number = number || 0;
		return new Intl.NumberFormat('en-US', {
			minimumFractionDigits: 0,
			maximumFractionDigits: decimal,
		}).format(number);
	},
	// currency: (amount: number | string, currency: string = types.Currencies.GHS) => {
	//     return `${types.CurrenciesSymbols[currency as types.Currencies]} ${format.number(+amount)}`;
	// },
	phoneNumber: (
		msisdn: string,
		type: 'national' | 'international' = 'international'
	) => {
		if (msisdn) {
			const regionCode = msisdn.includes('+') ? undefined : 'GH';
			const parsed = parsePhoneNumber(msisdn, { regionCode });
			return parsed.valid
				? parsed.number[type]
				: msisdn.length > 20
					? msisdn
					: msisdn.match(/.{1,3}/g)?.join(' ');
		}
		return '';
	},
};

export const seourl = (x: string) => {
	if (x) {
		x = latinize(x)
			.replace(/[`~!@#$%^&*()_\-+=\\[\]{};:'"\\|\\/,.<>?\s]/g, ' ')
			.toLowerCase()
			.replace(/^\s+|\s+$/gm, '')
			.replace(/\s+/g, '-');
		return x;
	}
	return x;
};

export const isJson = (str: any) => {
	try {
		JSON.parse(str);
		return true;
	} catch (error) {
		return false;
	}
};

export const togglePasswordShow = (e: any) => {
	const x = document.getElementById(e) as HTMLInputElement;
	if (x) {
		if (x.type === 'password') {
			x.setAttribute('type', 'text');
		} else {
			x.setAttribute('type', 'password');
		}
	}
};

export const cartTotal = (items: any) => {
	let total = 0;
	items.forEach((item: any) => {
		let price = item.price;
		Object.keys(item.product_options).forEach((po) => {
			if (item.product_options[po].price > 0) {
				price = item.product_options[po].price;
			}
		});
		let paidPrice =
			(item.paid_sides || []).length &&
			item.paid_sides.reduce(function (sum: number, ps: any) {
				return sum + ps.price;
			}, 0);
		total = total + (price + paidPrice) * (item.quantity || 1);
	});
	return total;
};

export const distanceMatrix = (
	pointA: any = {},
	pointB: any = {},
	onlyNumber: boolean = false
) => {
	const lat1 = pointA.lat;
	const lng1 = pointA.lng;
	const lat2 = pointB.lat;
	const lng2 = pointB.lng;
	if ((lat1 === lat2 && lng1 === lng2) || !lat1 || !lat2 || !lng1 || !lng2) {
		return onlyNumber ? 0 : '--km away';
	} else {
		const radlat1 = (Math.PI * lat1) / 180;
		const radlat2 = (Math.PI * lat2) / 180;
		const theta = lng1 - lng2;
		const radtheta = (Math.PI * theta) / 180;
		let dist: number =
			Math.sin(radlat1) * Math.sin(radlat2) +
			Math.cos(radlat1) * Math.cos(radlat2) * Math.cos(radtheta);
		if (dist > 1) {
			dist = 1;
		}
		dist = Math.acos(dist);
		dist = +(((dist * 180) / Math.PI) * 60 * 1.1515 * 1.609344).toFixed(3);
		return onlyNumber ? dist : `${dist}km away`;
	}
};

export const prepTime = (preparationTime: string) => {
	if (preparationTime === '00:00:00') {
		return 'Ready immediately';
	}
	return `Ready in ${`${moment.duration(preparationTime).asMinutes()} minutes`}`;
};

export const ucWords = (string: string) => {
	return (string || '').toLowerCase().replace(/\b[a-z]/g, function (letter) {
		return letter.toUpperCase();
	});
};

export const chunk = (inputArray: any, chunkSize: number) => {
	return inputArray.reduce((resultArray: any, item: any, index: number) => {
		const chunkIndex = Math.floor(index / chunkSize);
		if (!resultArray[chunkIndex]) {
			resultArray[chunkIndex] = []; // start a new chunk
		}
		resultArray[chunkIndex].push(item);
		return resultArray;
	}, []);
};

export const getTimeRemaining = (endtime: string) => {
	const total = Date.parse(endtime) - Date.parse(new Date().toString());
	const seconds = Math.floor((total / 1000) % 60);
	const minutes = Math.floor((total / 1000 / 60) % 60);
	const hours = Math.floor((total / (1000 * 60 * 60)) % 24);
	const days = Math.floor(total / (1000 * 60 * 60 * 24));

	return {
		total,
		days,
		hours: hours.toString().length > 1 ? hours : `0${hours}`,
		minutes: minutes.toString().length > 1 ? minutes : `0${minutes}`,
		seconds: seconds.toString().length > 1 ? seconds : `0${seconds}`,
	};
};

export const isOpen = (store: types.Store) => {
	// if (isLocal) {
	// 	return {
	// 		open: true,
	// 		text: `Open now`,
	// 	};
	// }
	let open = false;
	if (store?.address?.timeZone) {
		const td = moment();
		const day = td.format('dddd').toLowerCase();
		const today = td.format('YYYY-MM-DD');
		const hour = Number(td.format('HH'));
		const tomorrow = td.add(1, 'day').format('dddd').toLowerCase();
		const isDay = _.inRange(hour, 1, 19);
		const schedule =
			(store?.schedule || []).find((bh: any) => bh.day === day) ||
			({} as types.Schedule);
		const isOnline =
			typeof store.preferences.online !== 'undefined'
				? store.preferences.online
				: true;
		open = schedule.available
			? isOnline &&
				moment()
					.tz(store?.address.timeZone)
					.isBetween(
						`${today} ${schedule.opening}`,
						`${today} ${schedule.closing}`
					)
			: false;
		const tomorrowSchedule =
			(store?.schedule || []).find((bh: any) => bh.day === tomorrow) ||
			({} as types.Schedule);
		return {
			open,
			text: `Opens ${!isDay ? `tomorrow` : ``} at ${!isDay ? tomorrowSchedule.opening || '' : schedule.opening || ''}`,
		};
	}
	return { open, text: `Opens at` };
};

export const browser = async (link: string) => {
	if (isMobile) {
		await Browser.open({ url: link });
	} else {
		window.open(link, '_system');
	}
};

export const versionBreak = (v: string) => {
	if (v) {
		const vv = v.split('.');
		return +`${vv[0]}${vv[1]}${vv[2]}`;
	}
	return 0;
};

export const copyObject = (o: any) => JSON.parse(JSON.stringify(o));

export const isLocal = window.location.host.includes('localhost');
export const isStaging = window.location.host.includes('cr-mobile');
export const isStagingOrLocal = isLocal || isStaging;

export const requiredInput = { required: true, message: 'Field is required' };

export const venueName = (item: types.Store) => {
	if (!item) return '';
	let prefix = '';
	if (item?.code?.startsWith('PX')) prefix = 'Pie Xpress - ';
	if (item?.code?.startsWith('CR')) prefix = 'Chicken Republic - ';
	if (item?.code?.startsWith('CB')) prefix = 'Chopbox - ';
	return `${prefix}${item.name || ''}`;
};

export const scrollToTop = () =>
	window.scrollTo({ top: 0, behavior: 'smooth' });

export const myCity = () => {
	const location: { address: types.Address } = getStorageJson('location');
	const deliveryPartners: any = getStorageJson('deliveryPartners');
	let city = location.address.city ? location.address.city.toLowerCase() : '';
	const state = location.address.state.toLowerCase();
	const cities = _.uniq([
		...Object.keys(deliveryPartners[types.OrderOrigins.REPDASH]),
		...Object.keys(deliveryPartners[types.OrderOrigins.GLOVO]),
		...Object.keys(deliveryPartners[types.OrderOrigins.CHOWDECK]),
	]);
	if (!cities.includes(city) && cities.includes(state)) {
		city = state;
	}
	return city;
};

export const mapUser = (user: types.User) => {
	const settings = getStorageJson('settings') || {};
	const countries = settings.countries as types.Country[];
	if (user.id && countries) {
		// @ts-ignore
		user['country'] = countries?.find((c) => c.code === user.country);
	}
	return user;
};

export const checkKYC = (user: User) => {
	return {
		email: (user.preferences?.kyc || []).includes(user.email),
		phone: (user.preferences?.kyc || []).includes(user.phoneNumber),
	};
};

export const clearCache = (page = './') => {
	caches.keys().then((keys) => {
		Promise.all(keys.map((key) => caches.delete(key))).then(() => {
			redirect(page);
		});
	});
};

export const sanitize = {
	email: (email: string) => {
		if (!email) return 'a*****e@gmail.com';
		const [local, domain] = email.split('@');
		let sanitized;

		if (local.length <= 2) {
			sanitized = local[0] + '*'.repeat(local.length - 1);
		} else {
			sanitized =
				local[0] + '*'.repeat(local.length - 2) + local[local.length - 1];
		}

		return `${sanitized}@${domain}`;
	},
};

/**
 *
 * @param length number
 * @param strength string[] a: alphabets, n: numbers, s: alphabets
 * @return string
 */
export const randomCode = (length: number = 6, strength: string[] = ['n']) => {
	let result = '';
	let characters = '';
	if (strength.includes('a')) {
		characters += 'qwertyuplkjhgfdsazxcvbnmMNBVCXZASDFGHJKLPUYTREWQ';
	}
	if (strength.includes('n')) {
		characters += '123456789';
	}
	if (strength.includes('s')) {
		characters += '9@,!%#[{]}=():?';
	}
	let charactersLength = characters.length;
	for (let i = 0; i < length; i++) {
		result += characters.charAt(Math.floor(Math.random() * charactersLength));
	}
	return result;
};
